import { useTranslation } from 'react-i18next';
import useFetchBookingDotComCancellationPolicies from 'components/domain/bookingDotCom/useFetchBookingDotComCancellationPolicies';
import CustomSelectField from 'components/forms/customSelectField/CustomSelectField';
import { formatOptionLabelWithHighlight } from 'components/forms/multiSelectField/formatOptionLabel';
import FieldPlaceholder from 'pages/property/common/FieldPlaceholder';
import {
  CancellationPolicy,
  CancellationPolicyCategories,
} from 'models/bookingDotCom/CancellationPolicies';
import { orderBy } from 'utils/arrayUtils';

type GroupedCancellationPolicyOptions = {
  category: CancellationPolicyCategories;
  label: string;
  options: { label: string; value: number }[];
};

interface Props {
  name: string;
  label: React.ReactNode;
  placeholder?: string;
  colSmInput?: number;
  colSmLabel?: number;
  labelOnTop?: boolean;
  required?: boolean;
  isSearchable?: boolean;
  isClearable?: boolean;
  containerClassName?: string;
  leftAddon?: React.ReactNode;
  dataTestId?: string;
}

const CANCELLATION_POLICY_CATEGORY_ORDER = [
  CancellationPolicyCategories.POLICY_WITH_100_PERCENT_PENALTY,
  CancellationPolicyCategories.POLICY_WITH_50_PERCENT_PENALTY,
  CancellationPolicyCategories.POLICY_WITH_30_PERCENT_PENALTY,
  CancellationPolicyCategories.POLICY_WITH_PENALTY_IN_NIGHTS,
  CancellationPolicyCategories.POLICY_WITH_NO_PENALTY,
  CancellationPolicyCategories.UNDEFINED,
];

const BookingDotComCancellationPolicyField = ({
  name,
  label,
  placeholder,
  colSmInput = 7,
  colSmLabel = 5,
  labelOnTop = false,
  required = false,
  isSearchable = true,
  isClearable = false,
  containerClassName,
  leftAddon,
  dataTestId,
}: Props) => {
  const { t } = useTranslation();

  const {
    data: cancellationPolicies,
    isLoading: isLoadingCancellationPolicies,
  } = useFetchBookingDotComCancellationPolicies();

  if (isLoadingCancellationPolicies) {
    return (
      <FieldPlaceholder
        label={label}
        name={name}
        colSmInput={colSmInput}
        colSmLabel={colSmLabel}
        required={required}
      />
    );
  }

  const cancellationPoliciesGroupedByCategories = orderBy<CancellationPolicy>(
    cancellationPolicies ?? [],
    [
      {
        field: ({ category }) =>
          CANCELLATION_POLICY_CATEGORY_ORDER.indexOf(category),
        order: 'asc',
      },
      {
        field: 'name',
        order: 'asc',
      },
    ],
  ).reduce<GroupedCancellationPolicyOptions[]>((result, cancellationPolicy) => {
    const currentCategory = result[result.length - 1];

    if (
      !currentCategory ||
      currentCategory.category !== cancellationPolicy.category
    ) {
      return [
        ...result,
        {
          category: cancellationPolicy.category,
          label: t(
            `pageProperty.mainSettings.bookingDotComSettings.cancellationPolicyCategories.${cancellationPolicy.category}`,
          ),
          options: [
            { label: cancellationPolicy.name, value: cancellationPolicy.code },
          ],
        },
      ];
    }

    const { options } = currentCategory;

    options.push({
      label: cancellationPolicy.name,
      value: cancellationPolicy.code,
    });

    return result;
  }, []);

  return (
    <CustomSelectField
      label={label}
      name={name}
      placeholder={placeholder}
      options={[
        // @ts-expect-error
        { value: null, label: t('common.select') },
        // @ts-expect-error
        ...cancellationPoliciesGroupedByCategories,
      ]}
      formatOptionLabel={formatOptionLabelWithHighlight}
      containerClassName={containerClassName}
      colSmInput={colSmInput}
      colSmLabel={colSmLabel}
      labelOnTop={labelOnTop}
      isSearchable={isSearchable}
      isClearable={isClearable}
      leftAddon={leftAddon}
      data-testid={dataTestId}
    />
  );
};

export default BookingDotComCancellationPolicyField;
